import { View } from "ui/atoms/view";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { Button, ButtonText } from "ui/gluestack/button";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import { useHandleFluidIntake } from "../hooks/useHandleFluidIntake";
import { FormControlledTimePicker } from "ui/molecules/form/FormControlledTimePicker";
import { Controller, useWatch } from "react-hook-form";
import { FLUID_INTAKE__MAP } from "app/constants/ReportTypeConstant";
import { WaterIntakeButton } from "../molecules/WaterIntakeButton";
import { Text } from "react-native";
import { FluidIntakeEnums } from "app/services/api/types/forms/enums/FormEnums";

import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { FormControlledRadioGroup } from "ui/molecules/form/FormControlledRadioGroup";
import { FormControlledLargeTextInput } from "ui/molecules/form/FormControlledLargeTextInput";
import { requiredValidation } from "ui/utils/Validators";
import { FluidIntakeFormFields } from "app/services/api/types/forms/requests/FluidIntakeFormRequest";
import { formatEnumText } from "ui/utils/nameUtil";

interface Props {
  onClose: () => void;
}

export default function CreateFluidIntakeForm({ onClose }: Props) {
  const { control, submit, isSubmitting } = useHandleFluidIntake({
    onSuccess: onClose,
  });

  const intakeMethodOptions = Object.entries(FluidIntakeEnums).map(
    ([key, value]) => ({
      label: formatEnumText(key),
      value,
    }),
  );

  const intakeMethodValue = useWatch({ control, name: "intake_method" });
  const isOtherSelected =
    intakeMethodValue && intakeMethodValue[0] === FluidIntakeEnums.OTHER;

  return (
    <View>
      <View className="grid md:grid-cols-2 gap-8">
        <FormControlledDatePicker<FluidIntakeFormFields>
          id="recorded_date"
          name="recorded_date"
          control={control}
          label="Date of Intake"
          placeholder="00/00/0000"
          isRequired
          isDisabled={isSubmitting}
        />
        <FormControlledTimePicker<FluidIntakeFormFields>
          id="recorded_time"
          name="recorded_time"
          control={control}
          label="Time of Intake"
          placeholder="00/00/0000"
          isRequired
          isDisabled={isSubmitting}
        />
        <FormControlledInput<FluidIntakeFormFields>
          id="fluid_type"
          name="fluid_type"
          control={control}
          label="Type of Fluid"
          placeholder="Enter the type of fluid"
          isRequired
          isDisabled={isSubmitting}
        />
      </View>

      <View className="border border-gray-200 w-full my-12" />
      <View className="flex-col ">
        <Text className="font-medium">Consumed Amount</Text>
        <View className="flex-row flex-wrap gap-6 self-start mt-4">
          <Controller
            control={control}
            name="consumed_amount"
            render={({ field: { value, onChange } }) => (
              <>
                {FLUID_INTAKE__MAP.map(
                  ({ icon, label, value: intakeValue }, index) => (
                    <WaterIntakeButton
                      key={index}
                      icon={icon}
                      label={label}
                      selected={value === intakeValue}
                      onPress={() => {
                        onChange(intakeValue);
                      }}
                    />
                  ),
                )}
              </>
            )}
          />
        </View>
        <View className=" w-fit">
          <FormControlledLargeTextInput<FluidIntakeFormFields>
            id="consumed_amount"
            name="consumed_amount"
            control={control}
            placeholder="000"
            isRequired
            isDisabled={isSubmitting}
            unit="ml"
            rules={{
              required: requiredValidation("Amount"),
            }}
          />
        </View>
      </View>

      <View className="border border-gray-200 w-full my-12" />
      <Text className="font-medium mb-4">Intake Method</Text>
      <FormControlledRadioGroup
        id="intake_method"
        name="intake_method"
        control={control}
        options={intakeMethodOptions}
        isRequired
        isReadOnly={isSubmitting}
      />

      <View className="col-span-full mt-6">
        <FormControlledInputTextArea
          id="other_intake_method_details"
          name="other_intake_method_details"
          control={control}
          placeholder="If other, please specify intake method"
          isDisabled={isSubmitting}
          className="text-gray-500"
          rules={{
            required: isOtherSelected
              ? "This field is required when intake method is 'Other'"
              : false,
          }}
        />
      </View>

      <View className="border border-gray-200 w-full my-12" />
      <Text className="font-medium mb-4">Additional Information</Text>
      <FormControlledInputTextArea
        id="additional_information"
        name="additional_information"
        control={control}
        placeholder="Please enter other relevant information"
        isDisabled={isSubmitting}
        className="text-gray-500"
      />

      <View className="col-span-full">
        <View className="flex flex-row justify-end gap-x-4 mt-10">
          <Button
            action="primary"
            size="sm"
            variant="outline"
            onPress={onClose}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            action="primary"
            size="sm"
            onPress={submit}
            disabled={isSubmitting}
          >
            <ButtonText className="text-white">Log Intake</ButtonText>
          </Button>
        </View>
      </View>
    </View>
  );
}
